import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { Toaster } from "sonner";
import axios from "axios";

// Contexts
import { CartProvider } from "./contexts/CartContext";

// Pages
import HomePage from "./pages/HomePage";
import PhotographerLandingPage from "./pages/PhotographerLandingPage";
import BookingPage from "./pages/BookingPage";
import PricingPage from "./pages/PricingPage";
import DashboardLayout from "./pages/dashboard/DashboardLayout";
import DashboardHome from "./pages/dashboard/DashboardHome";
import RequestsPage from "./pages/dashboard/RequestsPage";
import CalendarPage from "./pages/dashboard/CalendarPage";
import GalleriesPage from "./pages/dashboard/GalleriesPage";
import GalleryDetailPage from "./pages/dashboard/GalleryDetailPage";
import ProfilePage from "./pages/dashboard/ProfilePage";
import BillingPage from "./pages/dashboard/BillingPage";
import ClientPortal from "./pages/ClientPortal";
import PublicGalleries from "./pages/PublicGalleries";
import PublicGalleryDetail from "./pages/PublicGalleryDetail";
import PublicAdminGalleryDetail from "./pages/PublicAdminGalleryDetail";
import PurchaseSuccess from "./pages/PurchaseSuccess";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";

// Dashboard sub-pages
import PaymentsPage from "./pages/dashboard/PaymentsPage";
import QuotesInvoicesPage from "./pages/dashboard/QuotesInvoicesPage";
import PromoCodesPage from "./pages/dashboard/PromoCodesPage";
import ContractsPage from "./pages/dashboard/ContractsPage";
import EmailTemplatesPage from "./pages/dashboard/EmailTemplatesPage";
import AnalyticsDashboard from "./pages/dashboard/AnalyticsDashboard";

// Public Pages
import ContractSignPage from "./pages/ContractSignPage";
import DownloadSuccess from "./pages/DownloadSuccess";

// Legal Pages
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import DMCAPolicy from "./pages/DMCAPolicy";

// Admin Pages
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminPhotographers from "./pages/admin/AdminPhotographers";
import AdminBilling from "./pages/admin/AdminBilling";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminProfile from "./pages/admin/AdminProfile";
import AdminGalleries from "./pages/admin/AdminGalleries";
import AdminGalleryDetail from "./pages/admin/AdminGalleryDetail";
import AdminTierManagement from "./pages/admin/AdminTierManagement";
import AdminStripe from "./pages/admin/AdminStripe";
import AdminSiteSettings from "./pages/admin/AdminSiteSettings";
import AdminActivityLog from "./pages/admin/AdminActivityLog";
import AdminSystemEmails from "./pages/admin/AdminSystemEmails";

// Payment Pages
import PaymentResultPage from "./pages/PaymentResultPage";

// Features & Help
import FeaturesPage from "./pages/FeaturesPage";
import HelpPage from "./pages/HelpPage";
import StudioContactPage from "./pages/StudioContactPage";
import PhotographerGuidePage from "./pages/PhotographerGuidePage";

// Client Portal
import ClientLogin from "./pages/ClientLogin";
import EnhancedClientPortal from "./pages/EnhancedClientPortal";
import InvoicePage from "./pages/InvoicePage";

// Onboarding
import {
  OnboardingLayout,
  OnboardingBusinessInfo,
  OnboardingAgreement,
  OnboardingProfileContent,
  OnboardingPortfolio,
  OnboardingPayments,
  OnboardingPublish
} from "./pages/onboarding";

// Analytics
import { trackPageView } from "./utils/analytics";

import "@/App.css";

// Determine the correct API URL based on current hostname
// If running on production domain (360lumina.com), use same-origin API calls
// Otherwise, use the preview environment URL
const getApiUrl = () => {
  const hostname = window.location.hostname;
  // Production domain - use relative path for same-origin requests
  if (hostname === '360lumina.com' || hostname === 'www.360lumina.com') {
    return '/api';
  }
  // Preview/development environment
  return `${process.env.REACT_APP_BACKEND_URL}/api`;
};

export const API = getApiUrl();

// Auth context
import { createContext, useContext } from "react";

export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/auth/me`, { headers });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/auth/logout`, {}, { headers });
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, refreshUser: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected route component with onboarding redirect
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if photographer needs to complete onboarding
  if (user.role === "photographer" && user.onboarding_status === "IN_PROGRESS") {
    // Already on onboarding page - allow access
    if (location.pathname.startsWith("/dashboard/onboarding")) {
      return children;
    }
    
    // Redirect to appropriate onboarding step
    const stepRoutes = {
      "BUSINESS_INFO": "/dashboard/onboarding",
      "AGREEMENT": "/dashboard/onboarding/agreement",
      "PROFILE_CONTENT": "/dashboard/onboarding/profile",
      "PORTFOLIO": "/dashboard/onboarding/portfolio",
      "PAYMENTS": "/dashboard/onboarding/payments",
      "READY_TO_PUBLISH": "/dashboard/onboarding/publish"
    };
    const targetRoute = stepRoutes[user.onboarding_step] || "/dashboard/onboarding";
    return <Navigate to={targetRoute} replace />;
  }

  return children;
};

// App router with session_id detection
function AppRouter() {
  const location = useLocation();

  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);

  // CRITICAL: Detect session_id synchronously during render
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  // Handle OAuth callback with success parameter
  if (location.pathname === '/auth/callback') {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/guide" element={<PhotographerGuidePage />} />
      <Route path="/studio-contact" element={<StudioContactPage />} />
      <Route path="/photographer/:businessName" element={<PhotographerLandingPage />} />
      <Route path="/book/:photographerId" element={<BookingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/portal/:token" element={<ClientPortal />} />
      <Route path="/portal/:token/invoice" element={<InvoicePage />} />
      
      {/* Enhanced Client Portal - Only accessible via photographer-specific URL */}
      <Route path="/client-login" element={<Navigate to="/" replace />} />
      <Route path="/client-login/:photographerId" element={<ClientLogin />} />
      <Route path="/client-portal/:sessionToken" element={<EnhancedClientPortal />} />
      
      <Route path="/galleries" element={<PublicGalleries />} />
      <Route path="/galleries/:galleryId" element={<PublicGalleryDetail />} />
      <Route path="/admin-gallery/:galleryId" element={<PublicAdminGalleryDetail />} />
      <Route path="/purchase/success" element={<PurchaseSuccess />} />
      <Route path="/download/success" element={<DownloadSuccess />} />
      
      {/* Contract signing (public) */}
      <Route path="/sign/:contractId" element={<ContractSignPage />} />
      
      {/* Legal Pages */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/dmca" element={<DMCAPolicy />} />
      
      {/* Payment result routes */}
      <Route path="/payment/success" element={<PaymentResultPage />} />
      <Route path="/payment/cancel" element={<PaymentResultPage />} />

      {/* Admin routes */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="photographers" element={<AdminPhotographers />} />
        <Route path="tiers" element={<AdminTierManagement />} />
        <Route path="stripe" element={<AdminStripe />} />
        <Route path="galleries" element={<AdminGalleries />} />
        <Route path="galleries/:galleryId" element={<AdminGalleryDetail />} />
        <Route path="billing" element={<AdminBilling />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="activity-log" element={<AdminActivityLog />} />
        <Route path="plans" element={<AdminBilling />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="site-settings" element={<AdminSiteSettings />} />
        <Route path="system-emails" element={<AdminSystemEmails />} />
        <Route path="profile" element={<AdminProfile />} />
      </Route>

      {/* Protected dashboard routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardHome />} />
        <Route path="requests" element={<RequestsPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="galleries" element={<GalleriesPage />} />
        <Route path="galleries/:galleryId" element={<GalleryDetailPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="payments" element={<PaymentsPage />} />
        <Route path="invoices" element={<Navigate to="/dashboard/quotes-invoices" replace />} />
        <Route path="quotes-invoices" element={<QuotesInvoicesPage />} />
        <Route path="promo-codes" element={<PromoCodesPage />} />
        <Route path="watermark" element={<Navigate to="/dashboard/profile" replace />} />
        <Route path="contracts" element={<ContractsPage />} />
        <Route path="email-templates" element={<EmailTemplatesPage />} />
        <Route path="analytics" element={<AnalyticsDashboard />} />
        <Route path="profile" element={<ProfilePage />} />
        
        {/* Onboarding routes (nested under dashboard for auth) */}
        <Route path="onboarding" element={<OnboardingLayout />}>
          <Route index element={<OnboardingBusinessInfo />} />
          <Route path="agreement" element={<OnboardingAgreement />} />
          <Route path="profile" element={<OnboardingProfileContent />} />
          <Route path="portfolio" element={<OnboardingPortfolio />} />
          <Route path="payments" element={<OnboardingPayments />} />
          <Route path="publish" element={<OnboardingPublish />} />
        </Route>
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <div className="App min-h-[100dvh] bg-background overflow-x-hidden w-full">
            <div className="noise-overlay" />
            <AppRouter />
            <Toaster 
              position="bottom-right"
              toastOptions={{
                style: {
                  background: 'hsl(0 0% 7%)',
                  border: '1px solid hsl(0 0% 15%)',
                  color: 'hsl(0 0% 93%)'
                }
              }}
            />
          </div>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
